import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import React, { lazy, memo, Suspense, useContext, useEffect, useMemo, useState } from 'react';
import DialogComponent from '../../dialog';
import DateFilter from '../DateFilter';
import { FILTER_LIST } from '../../../constants';
import { Typography } from '@vt/vt-ui';
import { HistoryContext } from '../HistoryPanel';
import { setTradeDialog, setTradeElementKey } from '../../../redux/reducer/dialog';
import { useFinancialMetricsWorker } from '../../../hooks/useFinancialMetricsWorker';

const DealsVirtualList = lazy(() => import('./components/DealsVirtualList'));

const InfoKey = [
  {
    key: 'credit',
    name: 'Credit'
  },
  {
    key: 'deposit',
    name: 'Deposit'
  },
  {
    key: 'withdrawal',
    name: 'Withdrawal'
  },
  {
    key: 'profit',
    name: 'Profit'
  },
  {
    key: 'swap',
    name: 'Swap'
  },
  {
    key: 'commission',
    name: 'Commission'
  },
  {
    key: 'balance',
    name: 'Balance'
  }
];

const Deals = memo(({ dialog, setDialog }) => {
  const { deals, loading } = useSelector(({ histories }) => histories);
  const { handleDate, submitDate } = useContext(HistoryContext);
  const initialInfo = {
    credit: 0,
    deposit: 0,
    withdrawal: 0,
    profit: 0,
    balance: 0,
    commission: 0,
    swap: 0
  };

  const [info, setInfo] = useState(initialInfo);

  const [marketOption, setMarketOption] = useState(7);

  // ใช้ Web Worker คำนวณ metrics ในเบื้องหลัง
  const { metrics, isCalculating: isCalculatingMetrics } = useFinancialMetricsWorker(deals, {
    enabled: true, // เปิดใช้งาน worker
    debounceMs: 100 // debounce 100ms เพื่อไม่ให้คำนวณบ่อยเกินไป
  });

  const dealsReverse = useMemo(() => {
    return [...deals].reverse();
  }, [deals]);

  // อัพเดท info เมื่อ metrics เปลี่ยน
  useEffect(() => {
    if (metrics) {
      setInfo(metrics);
    }
  }, [metrics]);

  const dispatch = useDispatch();
  const handleClose = () => {
    dispatch(setTradeDialog({ isOpen: false, type: '' }));
  };

  const setElementKey = (key) => {
    dispatch(setTradeElementKey(key));
  };

  const submitCustomDate = ({ from, to }) => {
    const fromUnix = typeof from === 'number' ? from : moment(from).startOf('day').utc().unix();
    const toUnix = typeof to === 'number' ? to : moment(to).endOf('day').utc().unix();
    submitDate({ from: fromUnix, to: toUnix });
    setDialog(false);
  };

  return (
    <div>
      <div className="history-dialog">
        <div className="mb-4 py-4 border-b border-[#404040] gap-y-2 flex flex-col">
          {InfoKey.map((e) => (
            <div key={e.key} className="grid grid-cols-2 mx-4 gap-y-2 text-sm text-[#c1c1c1]">
              <Typography variant="body3" className="text-[#e5e5e5]">
                {e.name}
              </Typography>
              <Typography variant="h5" className="text-end text-[#e5e5e5]">
                {parseFloat(info[e.key]).toFixed(2) ?? 0}
              </Typography>
            </div>
          ))}
        </div>
        <Suspense>
          <DealsVirtualList data={dealsReverse} />
        </Suspense>
      </div>
      <DialogComponent isOpen={dialog} onClose={() => setDialog(false)}>
        <div className="rounded-md bg-[#151515] border border-[#2e2e2e]">
          <div className="px-5 pt-5 flex justify-between items-center">
            <div></div>
            <div className="text-md text-[#e5e5e5] font-bold">Filter Deals History</div>
            <svg
              onClick={() => setDialog(false)}
              className="cursor-pointer"
              width="10"
              height="10"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg">
              <path
                d="M0.781396 16.0001C0.626858 16.0001 0.475783 15.9543 0.347281 15.8685C0.218778 15.7826 0.118621 15.6606 0.0594776 15.5178C0.000334661 15.3751 -0.0151369 15.218 0.0150198 15.0664C0.0451766 14.9148 0.119607 14.7756 0.228896 14.6664L14.6664 0.228853C14.8129 0.0823209 15.0117 0 15.2189 0C15.4261 0 15.6249 0.0823209 15.7714 0.228853C15.9179 0.375385 16.0002 0.574125 16.0002 0.781353C16.0002 0.988581 15.9179 1.18732 15.7714 1.33385L1.3339 15.7714C1.26141 15.844 1.17528 15.9016 1.08047 15.9408C0.985653 15.9801 0.884016 16.0002 0.781396 16.0001Z"
                fill="#9f9f9f"
              />
              <path
                d="M15.2189 16.0001C15.1162 16.0002 15.0146 15.9801 14.9198 15.9408C14.825 15.9016 14.7388 15.844 14.6664 15.7714L0.228853 1.33385C0.0823209 1.18732 0 0.988581 0 0.781353C0 0.574125 0.0823209 0.375385 0.228853 0.228853C0.375385 0.0823209 0.574125 0 0.781353 0C0.988581 0 1.18732 0.0823209 1.33385 0.228853L15.7714 14.6664C15.8806 14.7756 15.9551 14.9148 15.9852 15.0664C16.0154 15.218 15.9999 15.3751 15.9408 15.5178C15.8816 15.6606 15.7815 15.7826 15.653 15.8685C15.5245 15.9543 15.3734 16.0001 15.2189 16.0001Z"
                fill="#9f9f9f"
              />
            </svg>
          </div>
          <div className="pb-3">
            <div className="flex flex-col px-1 relative w-full">
              <div className="readonly top-[42px] left-0 w-full flex flex-col rounded-md">
                {FILTER_LIST.map((e, i) => (
                  <div
                    onClick={() => {
                      setMarketOption(e.key);
                      handleDate(e);
                    }}
                    key={i}
                    className="text-[#e5e5e5] list-filter text-sm px-8 h-[30px] flex items-center hover:bg-[#404040] hover:rounded-md transition-all cursor-pointer relative">
                    <span>
                      <svg
                        className={`absolute left-0 top-0 translate-y-1 translate-x-3 ${
                          marketOption === e.key ? 'block' : 'hidden'
                        }`}
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg">
                        <g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
                        <g
                          id="SVGRepo_tracerCarrier"
                          strokeLinecap="round"
                          strokeLinejoin="round"></g>
                        <g id="SVGRepo_iconCarrier">
                          {' '}
                          <path
                            d="M4 12.6111L8.92308 17.5L20 6.5"
                            stroke="#11EF9E"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"></path>{' '}
                        </g>
                      </svg>
                    </span>
                    <span className="ml-2">{e.name}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
          {marketOption === 5 && <DateFilter submitDate={submitCustomDate} />}
        </div>
      </DialogComponent>
    </div>
  );
});

Deals.displayName = 'Deals';

export default Deals;
